(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.NSPoeCalculatorCore = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var TIER_LIMITS = Object.freeze({ af: 15.4, at: 30, bt3: 60, bt4: 90 });
  var PD_CLASSES = Object.freeze([
    Object.freeze({ maxPdW: 13, ieeeType: '802.3af', psePowerW: 15.4, tier: 'af' }),
    Object.freeze({ maxPdW: 25.5, ieeeType: '802.3at', psePowerW: 30, tier: 'at' }),
    Object.freeze({ maxPdW: 51, ieeeType: '802.3bt Type 3', psePowerW: 60, tier: 'bt3' }),
    Object.freeze({ maxPdW: 71.3, ieeeType: '802.3bt Type 4', psePowerW: 90, tier: 'bt4' })
  ]);
  var PORT_CLASSES = Object.freeze([8, 16, 24, 48]);
  var BUDGET_TIERS = Object.freeze([120, 240, 370, 480, 600, 740, 960, 1440]);

  function finitePositive(value) {
    var number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
  }

  function finiteNonNegativeInteger(value) {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    var number = Number(value);
    return Number.isInteger(number) && number >= 0 ? number : null;
  }

  function structuredEvidenceStatus(product) {
    return String(product && product.poeSpecEvidenceStatus == null ? '' : product.poeSpecEvidenceStatus).trim();
  }

  function hasStructuredProductData(product) {
    var item = product || {};
    var status = structuredEvidenceStatus(item);
    if (/^Unable to verify$/i.test(status)) return false;
    if (status) return true;
    return [
      'poePoweredPortCount', 'poeBudgetW', 'poeMaxPseOutputW', 'poeIeeeStandard',
      'poe15WPortCount', 'poe30WPortCount', 'poe60WPortCount', 'poe90WPortCount'
    ].some(function (key) { return item[key] !== null && item[key] !== undefined && String(item[key]).trim() !== ''; });
  }

  function hasTrustedStructuredEvidence(product) {
    return /^(?:Official exact PID|Official family scope|First-party NSComm)$/i.test(structuredEvidenceStatus(product));
  }

  function parsePoePower(value) {
    var text = String(value == null ? '' : value).trim();
    var emptyResult = { totalBudgetW: null, maxPerPortW: null, ambiguous: false };
    if (!text || /^(?:none|n\/?a|no|non[- ]?poe|not applicable)$/i.test(text)) return emptyResult;

    var matches = [];
    var pattern = /(\d+(?:\.\d+)?)\s*w(?:atts?)?/gi;
    var match;
    while ((match = pattern.exec(text))) {
      var after = text.slice(match.index + match[0].length, match.index + match[0].length + 22);
      var before = text.slice(Math.max(0, match.index - 18), match.index);
      matches.push({
        value: Number(match[1]),
        perPort: /^\s*(?:(?:max(?:imum)?|up\s*to)\s+)?(?:per|each)\s+(?:poe\s+)?port/i.test(after) || /per\s+(?:poe\s+)?port\s*(?:up\s*to)?\s*$/i.test(before)
      });
    }

    if (!matches.length) return emptyResult;
    var perPortValues = matches.filter(function (item) { return item.perPort; }).map(function (item) { return item.value; });
    var totalValues = matches.filter(function (item) { return !item.perPort; }).map(function (item) { return item.value; });
    var ambiguous = totalValues.length > 1 || perPortValues.length > 1;

    return {
      totalBudgetW: !ambiguous && totalValues.length === 1 ? totalValues[0] : null,
      maxPerPortW: !ambiguous && perPortValues.length === 1 ? perPortValues[0] : null,
      ambiguous: ambiguous
    };
  }

  function parsePoePorts(value) {
    var counts = explicitPoePortCounts(value);
    return counts.length === 1 ? counts[0] : null;
  }

  function explicitPoePortCounts(value) {
    var text = String(value == null ? '' : value);
    var patterns = [
      /(?:^|[\s,;])([0-9]+)\s*(?:x|×)?\s*(?:(?:[0-9./]+\s*(?:Mbps|Gbps|GE|FE|G|M)|Multi[- ]?Gigabit|GE|FE|Gigabit(?:\s+Ethernet)?|Ethernet|RJ-?45)\s*)*\(?U?PoE(?:\+\+|\+)?/gi,
      /\b([0-9]+)[ -]Port\b(?=[^,;]{0,80}\bU?PoE(?:\+\+|\+)?)/gi,
      /\b([0-9]+)\s+ports?\s+(?:support(?:ing)?\s+)?U?PoE(?:\+\+|\+)?/gi
    ];
    var counts = [];
    patterns.forEach(function (pattern) {
      var match;
      while ((match = pattern.exec(text))) {
        var countIndex = match.index + match[0].indexOf(match[1]);
        if (/\bLayer\s*$/i.test(text.slice(Math.max(0, countIndex - 12), countIndex))) continue;
        var count = Number(match[1]);
        if (count > 0 && counts.indexOf(count) === -1) counts.push(count);
      }
    });
    return counts;
  }

  function inferTier(watts) {
    var value = finitePositive(watts);
    if (value === null || value > TIER_LIMITS.bt4) return null;
    if (value <= TIER_LIMITS.af) return 'af';
    if (value <= TIER_LIMITS.at) return 'at';
    if (value <= TIER_LIMITS.bt3) return 'bt3';
    return 'bt4';
  }

  function classifyPdPower(watts) {
    var value = finitePositive(watts);
    if (value === null) return null;
    for (var index = 0; index < PD_CLASSES.length; index += 1) {
      if (value <= PD_CLASSES[index].maxPdW) {
        return {
          ieeeType: PD_CLASSES[index].ieeeType,
          psePowerW: PD_CLASSES[index].psePowerW
        };
      }
    }
    if (value <= TIER_LIMITS.bt4) {
      return {
        ieeeType: '802.3bt Type 4',
        psePowerW: 90,
        tier: 'bt4',
        requiresConfirmation: true
      };
    }
    return null;
  }

  function suggestPortClass(requiredPorts) {
    var ports = Math.ceil(Number(requiredPorts) || 0);
    for (var index = 0; index < PORT_CLASSES.length; index += 1) {
      if (ports <= PORT_CLASSES[index]) return PORT_CLASSES[index] + '-port class';
    }
    return 'Custom / stack';
  }

  function suggestBudgetTier(requiredBudgetW) {
    var budget = Number(requiredBudgetW) || 0;
    for (var index = 0; index < BUDGET_TIERS.length; index += 1) {
      if (budget <= BUDGET_TIERS[index]) return BUDGET_TIERS[index] + 'W+';
    }
    return 'Custom / stack';
  }

  function calculateRequirements(devices, options) {
    if (!Array.isArray(devices) || devices.length === 0) {
      throw new Error('Enter at least one device row.');
    }
    var settings = options || {};
    var headroomPercent = settings.headroomPercent == null ? 25 : Number(settings.headroomPercent);
    var reservedPorts = settings.reservedPorts == null ? 0 : Number(settings.reservedPorts);
    if (!Number.isFinite(headroomPercent) || headroomPercent < 0 || headroomPercent > 300) {
      throw new Error('Headroom percent must be between 0 and 300.');
    }
    if (!Number.isInteger(reservedPorts) || reservedPorts < 0) {
      throw new Error('Reserved ports must be a non-negative integer.');
    }

    var deviceCount = 0;
    var rawPowerW = 0;
    var highestWatts = 0;
    var requiredPsePorts = Object.create(null);
    devices.forEach(function (device, index) {
      var row = index + 1;
      var quantity = Number(device && device.quantity);
      var watts = Number(device && device.watts);
      if (!Number.isInteger(quantity) || quantity <= 0) {
        throw new Error('Row ' + row + ': quantity must be a positive whole number.');
      }
      if (!Number.isFinite(watts) || watts <= 0) {
        throw new Error('Row ' + row + ': watts must be a positive number.');
      }
      if (watts > TIER_LIMITS.bt4) {
        throw new Error('Row ' + row + ': watts above 90W require manual confirmation.');
      }
      deviceCount += quantity;
      rawPowerW += quantity * watts;
      highestWatts = Math.max(highestWatts, watts);
      var classification = classifyPdPower(watts);
      if (classification) {
        var pseKey = String(classification.psePowerW);
        requiredPsePorts[pseKey] = (requiredPsePorts[pseKey] || 0) + quantity;
      }
    });

    var highestClass = classifyPdPower(highestWatts);
    var sortedPsePorts = Object.keys(requiredPsePorts).sort(function (a, b) { return Number(a) - Number(b); }).reduce(function (result, key) {
      result[key] = requiredPsePorts[key];
      return result;
    }, {});
    var recommendedBudgetW = Math.ceil(rawPowerW * (1 + headroomPercent / 100) * 10) / 10;

    return {
      safetyFactorPercent: headroomPercent,
      deviceCount: deviceCount,
      rawPowerW: rawPowerW,
      recommendedBudgetW: recommendedBudgetW,
      requiredPorts: deviceCount + reservedPorts,
      highestTier: highestClass ? (highestClass.tier || PD_CLASSES.find(function (item) { return item.ieeeType === highestClass.ieeeType; }).tier) : null,
      maxDeviceLoadW: highestWatts,
      requiredIeeeType: highestClass ? highestClass.ieeeType : null,
      requiredPsePowerW: highestClass ? highestClass.psePowerW : null,
      requiredPsePorts: sortedPsePorts,
      suggestedPortClass: suggestPortClass(deviceCount + reservedPorts),
      suggestedBudgetTier: suggestBudgetTier(recommendedBudgetW),
      primaryGateStatus: highestClass && !highestClass.requiresConfirmation ? 'ok' : 'confirm',
      primaryGateResult: highestClass
        ? (highestClass.requiresConfirmation
          ? 'Confirm actual PD available power is no more than 71.3W; matching 90W PSE products are shown as conditional.'
          : 'OK - per-port class identified')
        : 'Engineering confirmation - no IEEE single-port class identified'
    };
  }

  function explicitMaxPerPortFromText(value) {
    var text = String(value == null ? '' : value);
    var match = text.match(/(\d+(?:\.\d+)?)\s*w(?:atts?)?\s*(?:\/\s*port|per\s+(?:poe\s+)?port)\b/i);
    return match ? finitePositive(match[1]) : null;
  }

  function maxPerPortFromText(value) {
    var text = String(value == null ? '' : value);
    var parsed = parsePoePower(text);
    if (parsed.maxPerPortW) return parsed.maxPerPortW;
    if (/802\.3bt\s*type\s*4|\bbt4\b|\bupoe\+(?!\+)/i.test(text)) return 90;
    if (/802\.3bt\s*type\s*3|\bbt3\b|poe\+\+|\bupoe\b/i.test(text)) return 60;
    if (/802\.3at|poe\+(?!\+)/i.test(text)) return 30;
    if (/802\.3af|\bpoe\b/i.test(text)) return 15.4;
    return null;
  }

  function resolveProductFacts(product) {
    var item = product || {};
    if (hasStructuredProductData(item)) {
      return {
        totalBudgetW: finitePositive(item.poeBudgetW),
        maxPerPortW: finitePositive(item.poeMaxPseOutputW),
        poePorts: finiteNonNegativeInteger(item.poePoweredPortCount),
        ambiguous: false
      };
    }
    var poePowerText = String(item.poePower == null ? '' : item.poePower).trim();
    var dedicatedBudget = /^\d+(?:\.\d+)?$/.test(poePowerText) ? finitePositive(poePowerText) : null;
    var power = item.totalBudgetW != null
      ? { totalBudgetW: finitePositive(item.totalBudgetW), maxPerPortW: null, ambiguous: false }
      : dedicatedBudget !== null
        ? { totalBudgetW: dedicatedBudget, maxPerPortW: null, ambiguous: false }
        : parsePoePower(item.poePower);
    var explicitMax = finitePositive(item.maxPerPortW) || explicitMaxPerPortFromText(item.descriptionExcerpt);
    var maxPerPortW = explicitMax || power.maxPerPortW || maxPerPortFromText(item.poePorts) || maxPerPortFromText(item.detail) || maxPerPortFromText(item.title);
    var counts = explicitPoePortCounts(item.detail).concat(explicitPoePortCounts(item.title));
    var dedicatedPorts = finitePositive(item.poePorts);
    if (dedicatedPorts !== null) counts.push(dedicatedPorts);
    else counts = counts.concat(explicitPoePortCounts(item.poePorts));
    counts = counts.filter(function (count, index, all) { return all.indexOf(count) === index; });
    var poePorts = counts.length === 1 ? counts[0] : null;
    return {
      totalBudgetW: power.totalBudgetW,
      maxPerPortW: maxPerPortW,
      poePorts: poePorts,
      ambiguous: Boolean(item.ambiguous || power.ambiguous || counts.length > 1)
    };
  }

  function resolveDeploymentFacts(product) {
    var item = product || {};
    if (hasStructuredProductData(item)) {
      var structuredManagement = String(item.managementType == null ? '' : item.managementType).trim().toLowerCase();
      var structuredEnvironment = String(item.environmentType == null ? '' : item.environmentType).trim().toLowerCase();
      var uplinkMap = {
        '1g rj45': '1g-rj45',
        '1g sfp': '1g-sfp',
        '10g sfp+': '10g-sfp-plus',
        'combo rj45/sfp': 'combo'
      };
      var structuredUplinks = String(item.uplinkProfile == null ? '' : item.uplinkProfile).split(';').map(function (value) {
        return uplinkMap[value.trim().toLowerCase()] || null;
      }).filter(function (value, index, all) { return value && all.indexOf(value) === index; });
      return {
        management: /^(?:managed|unmanaged)$/.test(structuredManagement) ? structuredManagement : null,
        environment: /^(?:commercial|industrial)$/.test(structuredEnvironment) ? structuredEnvironment : null,
        uplinks: structuredUplinks
      };
    }
    var text = [item.title, item.detail].filter(Boolean).join(' ');
    var management = null;
    if (/\bunmanaged\b/i.test(text)) management = 'unmanaged';
    else if (/\b(?:web[- ]?managed|lightly[- ]?managed|fully[- ]?managed|managed)\b/i.test(text)) management = 'managed';

    var environment = null;
    if (/\b(?:industrial|hardened|din[- ]rail)\b/i.test(text)) environment = 'industrial';
    else if (/\bcommercial\b/i.test(text)) environment = 'commercial';

    var uplinks = [];
    if (/\bcombo\b[^,;]{0,24}\buplinks?\b/i.test(text)) uplinks.push('combo');
    if (/(?:\b10\s*(?:G|GE|Gbps)\b[^,;]{0,20}\bSFP\+|\bSFP\+\b[^,;]{0,20}\b10\s*(?:G|GE|Gbps))[^,;]{0,20}\buplinks?\b/i.test(text)) uplinks.push('10g-sfp-plus');
    if (/(?:\b1\s*(?:G|GE|Gbps)\b|\bGE\b)[^,;]{0,20}\bSFP(?!\+)\b[^,;]{0,20}\buplinks?\b/i.test(text)) uplinks.push('1g-sfp');
    if (/(?:\b1\s*(?:G|GE|Gbps)\b|\bGE\b)[^,;]{0,20}\bRJ-?45\b[^,;]{0,20}\buplinks?\b/i.test(text)) uplinks.push('1g-rj45');

    return { management: management, environment: environment, uplinks: uplinks.filter(function (value, index, all) { return all.indexOf(value) === index; }) };
  }

  function explicitUniformPseCapacity(product, facts) {
    var item = product || {};
    var excerptCapacity = explicitMaxPerPortFromText(item.descriptionExcerpt);
    var excerptCounts = explicitPoePortCounts(item.descriptionExcerpt);
    if (excerptCapacity !== null && facts && excerptCounts.indexOf(facts.poePorts) !== -1) return excerptCapacity;
    var text = [item.title, item.detail, item.poePorts].filter(Boolean).join(' ');
    var unique = explicitPseCapacities(text);
    if (unique.length !== 1 || !facts || unique[0].count !== facts.poePorts) return null;
    return unique[0].capacity;
  }

  function explicitPseCapacities(value) {
    var text = String(value == null ? '' : value);
    var pattern = /\b(\d+)\s*(?:x|×|[- ]port(?:s)?)?\s*(?:(?:[0-9./]+\s*(?:Mbps|Gbps|GE|FE|G|M)|GE|FE|Gigabit(?:\s+Ethernet)?|Ethernet|RJ-?45)\s*)*\(?\s*(U?PoE(?:\+\+|\+)?|802\.3(?:af|at|bt)(?:\s*Type\s*[34])?)/gi;
    var capacities = [];
    var match;
    while ((match = pattern.exec(text))) {
      var count = Number(match[1]);
      var capacity = maxPerPortFromText(match[2]);
      if (count > 0 && capacity) capacities.push({ count: count, capacity: capacity });
    }
    return capacities.filter(function (itemValue, index, all) {
      return all.findIndex(function (candidate) { return candidate.count === itemValue.count && candidate.capacity === itemValue.capacity; }) === index;
    });
  }

  function resolveMixedPoeCapacity(product) {
    var item = product || {};
    var groups = explicitPseCapacities(item.detail);
    var byCapacity = Object.create(null);
    groups.forEach(function (group) {
      byCapacity[group.capacity] = (byCapacity[group.capacity] || 0) + group.count;
    });
    var capacities = Object.keys(byCapacity).map(function (capacity) {
      return { capacity: Number(capacity), count: byCapacity[capacity] };
    }).sort(function (a, b) { return a.capacity - b.capacity; });
    if (capacities.length < 2) return null;

    var totalPorts = capacities.reduce(function (sum, group) { return sum + group.count; }, 0);
    var titleCounts = explicitPoePortCounts(item.title);
    var dedicatedPorts = finitePositive(item.poePorts);
    var conflicts = titleCounts.some(function (count) { return count !== totalPorts; }) ||
      (dedicatedPorts !== null && dedicatedPorts !== totalPorts);
    return {
      capacities: capacities,
      poePorts: totalPorts,
      maxPerPortW: capacities[capacities.length - 1].capacity,
      ambiguous: conflicts
    };
  }

  function mixedCapacityMeetsRequirements(mixed, requirements) {
    if (!mixed || mixed.ambiguous) return false;
    var required = Object.assign({}, requirements.requiredPsePorts || {});
    var counted = Object.keys(required).reduce(function (sum, power) { return sum + (Number(required[power]) || 0); }, 0);
    if (!counted) required[String(requirements.requiredPsePowerW)] = Number(requirements.requiredPorts) || 0;
    else if (Number(requirements.requiredPorts) > counted) required['15.4'] = (Number(required['15.4']) || 0) + Number(requirements.requiredPorts) - counted;

    return [90, 60, 30, 15.4].every(function (threshold) {
      var needed = Object.keys(required).reduce(function (sum, power) {
        return Number(power) >= threshold ? sum + (Number(required[power]) || 0) : sum;
      }, 0);
      var available = mixed.capacities.reduce(function (sum, group) {
        return group.capacity >= threshold ? sum + group.count : sum;
      }, 0);
      return available >= needed;
    });
  }

  function structuredCapacityMeetsRequirements(product, requirements, requiredPerPortW) {
    var tierCounts = {
      '15.4': finiteNonNegativeInteger(product.poe15WPortCount),
      '30': finiteNonNegativeInteger(product.poe30WPortCount),
      '60': finiteNonNegativeInteger(product.poe60WPortCount),
      '90': finiteNonNegativeInteger(product.poe90WPortCount)
    };
    var required = Object.assign({}, requirements.requiredPsePorts || {});
    var counted = Object.keys(required).reduce(function (sum, power) { return sum + (Number(required[power]) || 0); }, 0);
    if (!counted) required[String(requiredPerPortW)] = Number(requirements.requiredPorts) || 0;
    else if (Number(requirements.requiredPorts) > counted) required['15.4'] = (Number(required['15.4']) || 0) + Number(requirements.requiredPorts) - counted;

    var unknown = false;
    var sufficient = [90, 60, 30, 15.4].every(function (threshold) {
      var needed = Object.keys(required).reduce(function (sum, power) {
        return Number(power) >= threshold ? sum + (Number(required[power]) || 0) : sum;
      }, 0);
      if (!needed) return true;
      var available = tierCounts[String(threshold)];
      if (available === null) {
        unknown = true;
        return false;
      }
      return available >= needed;
    });
    return unknown ? null : sufficient;
  }

  function requiredPerPort(requirements) {
    var devices = requirements && Array.isArray(requirements.devices) ? requirements.devices : [];
    var deviceMax = devices.reduce(function (maximum, device) {
      var watts = finitePositive(device && device.watts);
      return watts === null ? maximum : Math.max(maximum, watts);
    }, 0);
    return deviceMax || TIER_LIMITS[requirements && requirements.highestTier] || null;
  }

  function evaluatePreferred(product, requirements) {
    var facts = resolveProductFacts(product);
    var missing = [];
    if (facts.ambiguous) missing.push('PoE budget depends on an unverified configuration');
    if (facts.totalBudgetW === null) missing.push('total PoE budget is missing');
    if (facts.poePorts === null) missing.push('PoE port count is missing');
    if (facts.maxPerPortW === null) missing.push('per-port PoE capability is missing');
    if (missing.length) return { status: 'confirm', reasons: missing, facts: facts };

    var failures = [];
    if (facts.totalBudgetW < requirements.recommendedBudgetW) failures.push('PoE budget is too low');
    if (facts.poePorts < requirements.requiredPorts) failures.push('PoE port count is too low');
    var neededPerPort = requiredPerPort(requirements);
    if (neededPerPort !== null && facts.maxPerPortW < neededPerPort) failures.push('per-port PoE capability is too low');

    var professional = requirements.professional || {};
    ['accessSpeedGbps', 'uplinkCount', 'uplinkSpeedGbps'].forEach(function (key) {
      if (professional[key] != null) {
        if (product[key] == null) missing.push(key + ' is missing');
        else if (Number(product[key]) < Number(professional[key])) failures.push(key + ' is too low');
      }
    });
    if (professional.requireRedundantPsu) {
      if (product.redundantPsu == null) missing.push('redundant PSU capability is missing');
      else if (!product.redundantPsu) failures.push('redundant PSU is unavailable');
    }
    if (professional.brandPreference && String(product.vendor || '').toLowerCase() !== String(professional.brandPreference).toLowerCase()) {
      failures.push('brand does not match the selected preference');
    }
    if (missing.length) return { status: 'confirm', reasons: missing, facts: facts };
    if (failures.length) return { status: 'not_suitable', reasons: failures, facts: facts };
    return { status: 'suitable', reasons: [], facts: facts };
  }

  function calculateExpansion(productFacts, requirements) {
    var facts = productFacts || {};
    var devices = requirements && Array.isArray(requirements.devices) ? requirements.devices : [];
    var remainingPorts = Math.max(0, Math.floor(Number(facts.poePorts) - Number(requirements.requiredPorts)));
    var remainingBudgetW = Math.max(0, Number(facts.totalBudgetW) - Number(requirements.recommendedBudgetW));
    var maxPerPortW = Number(facts.maxPerPortW);
    var seen = Object.create(null);

    return devices.reduce(function (results, device) {
      var name = String(device.name || 'PoE device').trim() || 'PoE device';
      var watts = Number(device.watts);
      var key = name.toLowerCase() + '\u0000' + watts;
      if (seen[key]) return results;
      seen[key] = true;
      var additionalCount = 0;
      if (Number.isFinite(watts) && watts > 0 && Number.isFinite(maxPerPortW) && watts <= maxPerPortW) {
        additionalCount = Math.max(0, Math.min(remainingPorts, Math.floor(remainingBudgetW / watts)));
      }
      results.push({ name: name, watts: watts, additionalCount: additionalCount });
      return results;
    }, []);
  }

  function normalizeBrand(value) {
    var brand = String(value == null ? '' : value).trim();
    return brand || 'Other';
  }

  function groupByBrand(products) {
    var groups = Object.create(null);
    (products || []).forEach(function (product) {
      var brand = normalizeBrand(product.vendor);
      var key = brand.toLowerCase();
      if (!groups[key]) groups[key] = { brand: brand, all: [] };
      groups[key].all.push(product);
    });
    return Object.keys(groups).map(function (key) {
      var group = groups[key];
      return {
        brand: group.brand,
        products: group.all.slice(0, 10),
        initialVisible: Math.min(3, group.all.length, 10),
        totalMatches: group.all.length
      };
    }).sort(function (a, b) { return a.brand.localeCompare(b.brand); });
  }

  function rankProducts(products, requirements) {
    var current = [];
    var future = [];
    var excludedCount = 0;
    (Array.isArray(products) ? products : []).forEach(function (product) {
      var verdict = evaluatePreferred(product, requirements);
      if (verdict.status !== 'suitable') {
        excludedCount += 1;
        return;
      }
      var expansion = calculateExpansion(verdict.facts, requirements);
      var rankedProduct = Object.assign({}, product, verdict.facts, { expansion: expansion });
      var futureReady = expansion.length > 0 && expansion.every(function (item) { return item.additionalCount >= 1; });
      (futureReady ? future : current).push(rankedProduct);
    });

    function compareFit(a, b) {
      var budgetDelta = (a.totalBudgetW - requirements.recommendedBudgetW) - (b.totalBudgetW - requirements.recommendedBudgetW);
      if (budgetDelta) return budgetDelta;
      var portDelta = (a.poePorts - requirements.requiredPorts) - (b.poePorts - requirements.requiredPorts);
      if (portDelta) return portDelta;
      return String(a.title || '').localeCompare(String(b.title || ''));
    }

    current.sort(compareFit);
    future.sort(compareFit);
    return {
      current: groupByBrand(current),
      future: groupByBrand(future),
      excludedCount: excludedCount
    };
  }

  function requiredPsePowerW(devices) {
    var watts = (devices || []).reduce(function (max, device) { return Math.max(max, Number(device.watts) || 0); }, 0);
    var classification = classifyPdPower(watts);
    return classification ? classification.psePowerW : null;
  }

  function recommendationProducts(products, requirements, limit) {
    return recommendationMatches(products, requirements, limit).verified;
  }

  function recommendationMatches(products, requirements, limit) {
    var perPort = requiredPsePowerW(requirements.devices);
    if (requirements.requiredPsePowerW != null) perPort = requirements.requiredPsePowerW;
    if (perPort === null) return { verified: [], conditional: [], excludedCount: Array.isArray(products) ? products.length : 0 };
    var seen = Object.create(null);
    var evaluated = (products || []).reduce(function (matches, product) {
      if (!product || typeof product !== 'object') {
        matches.excludedCount += 1;
        return matches;
      }
      var key = String(product.id || product.url || '');
      if (!key || seen[key] || !product.url) return matches;
      seen[key] = true;
      var structured = hasStructuredProductData(product);
      var evidenceStatus = structuredEvidenceStatus(product);
      if (/^Contradicted$/i.test(evidenceStatus)) {
        matches.excludedCount += 1;
        return matches;
      }
      var facts = resolveProductFacts(product);
      var mixedCapacity = structured ? null : resolveMixedPoeCapacity(product);
      if (mixedCapacity && !mixedCapacity.ambiguous) {
        var powerAmbiguous = Boolean(product.ambiguous || parsePoePower(product.poePower).ambiguous);
        facts = Object.assign({}, facts, {
          poePorts: mixedCapacity.poePorts,
          maxPerPortW: mixedCapacity.maxPerPortW,
          ambiguous: powerAmbiguous
        });
      }
      if (facts.ambiguous || facts.totalBudgetW === null || facts.poePorts === null || facts.maxPerPortW === null ||
          facts.totalBudgetW < requirements.recommendedBudgetW || facts.poePorts < requirements.requiredPorts || facts.maxPerPortW < perPort) {
        matches.excludedCount += 1;
        return matches;
      }

      var deployment = resolveDeploymentFacts(product);
      var conditionalReasons = [];
      var mismatch = false;
      if (structured) {
        var structuredCapacity = structuredCapacityMeetsRequirements(product, requirements, perPort);
        if (structuredCapacity !== true) mismatch = true;
        if (!hasTrustedStructuredEvidence(product)) conditionalReasons.push('structured evidence status needs confirmation');
      } else {
        conditionalReasons.push('technical values come from legacy text parsing');
      }
      if (requirements.primaryGateStatus === 'confirm') {
        conditionalReasons.push('the entered PD load is above the 71.3W IEEE Type 4 available-power threshold');
      }
      if (structured) {
        // Cumulative structured tier counts above are the authoritative port-allocation gate.
      } else if (mixedCapacity) {
        if (!mixedCapacityMeetsRequirements(mixedCapacity, requirements)) mismatch = true;
      } else if (perPort > 30) {
        var uniformCapacity = explicitUniformPseCapacity(product, facts);
        if (uniformCapacity === null) conditionalReasons.push('high-power port quantity needs confirmation');
        else if (uniformCapacity < perPort) mismatch = true;
      }
      [['management', requirements.management], ['environment', requirements.environment]].forEach(function (entry) {
        var selected = entry[1];
        if (!selected || selected === 'not-specified') return;
        if (deployment[entry[0]] === null) conditionalReasons.push(entry[0] + ' needs confirmation');
        else if (deployment[entry[0]] !== selected) mismatch = true;
      });
      if (requirements.uplink && requirements.uplink !== 'not-specified') {
        if (!deployment.uplinks.length) conditionalReasons.push('uplink needs confirmation');
        else if (deployment.uplinks.indexOf(requirements.uplink) === -1) mismatch = true;
      }
      if (mismatch) {
        matches.excludedCount += 1;
        return matches;
      }

      var verificationStatus = conditionalReasons.length ? 'conditional' : 'verified';
      var selectedLabels = [requirements.management, requirements.environment, requirements.uplink].filter(function (value) { return value && value !== 'not-specified'; });
      var reason = 'Covers at least ' + requirements.recommendedBudgetW + 'W and ' + requirements.requiredPorts + ' powered ports with the required per-port tier.';
      if (selectedLabels.length) reason += ' Selected project filters: ' + selectedLabels.join(', ') + '.';
      if (conditionalReasons.length) reason += ' Confirm: ' + conditionalReasons.join('; ') + '.';
      matches[verificationStatus].push(Object.assign({}, product, facts, deployment, {
        verificationStatus: verificationStatus,
        fitReason: reason
      }));
      return matches;
    }, { verified: [], conditional: [], excludedCount: 0 });

    function sortMatches(list) { return list.sort(function (a, b) {
      if (Boolean(a.available) !== Boolean(b.available)) return a.available ? -1 : 1;
      var portDelta = (a.poePorts - requirements.requiredPorts) - (b.poePorts - requirements.requiredPorts);
      if (portDelta) return portDelta;
      var budgetDelta = (a.totalBudgetW - requirements.recommendedBudgetW) - (b.totalBudgetW - requirements.recommendedBudgetW);
      return budgetDelta || Number(a.priceMinor || 0) - Number(b.priceMinor || 0) || String(a.id).localeCompare(String(b.id));
    }); }
    var maximum = Math.min(6, Math.max(1, Number(limit) || 6));
    sortMatches(evaluated.verified);
    sortMatches(evaluated.conditional);
    evaluated.verified = evaluated.verified.slice(0, maximum);
    evaluated.conditional = evaluated.conditional.slice(0, Math.max(0, maximum - evaluated.verified.length));
    return evaluated;
  }

  return Object.freeze({
    parsePoePower: parsePoePower,
    parsePoePorts: parsePoePorts,
    inferTier: inferTier,
    classifyPdPower: classifyPdPower,
    suggestPortClass: suggestPortClass,
    suggestBudgetTier: suggestBudgetTier,
    calculateRequirements: calculateRequirements,
    resolveProductFacts: resolveProductFacts,
    resolveDeploymentFacts: resolveDeploymentFacts,
    evaluatePreferred: evaluatePreferred,
    calculateExpansion: calculateExpansion,
    groupByBrand: groupByBrand,
    rankProducts: rankProducts,
    recommendationProducts: recommendationProducts,
    recommendationMatches: recommendationMatches,
    requiredPsePowerW: requiredPsePowerW
  });
});
